import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertCircle,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Pause,
  Play,
  Volume2,
  VolumeX,
} from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter, useSearch } from '@tanstack/react-router';
import { overlay } from 'overlay-kit';

import { courseQueries, postQuizzesAnswer } from '@/entities/course';
import { useCaptureAnalyticsEvent } from '@/shared/analytics/events';
import type { ErrorResponse } from '@/shared/types/api';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import {
  Reel,
  ReelContent,
  ReelItem,
  ReelProgress,
} from '@/shared/ui/kibo-ui/reel';
import { useToast } from '@/shared/ui/toast-provider';
import { cn } from '@/shared/utils';
import {
  pickPreferredMediaSource,
  useQuizAudioPlayer,
} from '@/pages/quiz/model/use-quiz-audio-player';
import { DescriptionModal } from '@/pages/quiz/ui/description-modal';

const formatTimestamp = (seconds: number) => {
  if (!Number.isFinite(seconds) || seconds < 0) return '00:00';
  const wholeSeconds = Math.floor(seconds);
  const mins = Math.floor(wholeSeconds / 60)
    .toString()
    .padStart(2, '0');
  const secs = Math.floor(wholeSeconds % 60)
    .toString()
    .padStart(2, '0');
  return `${mins}:${secs}`;
};

const ANSWER_AUTO_SUBMIT_DELAY_MS =
  import.meta.env.VITE_PUBLIC_APP_ENV === 'staging' ? 10_000 : 60_000;
const AUTO_SUBMIT_DELAY_SECONDS = Math.floor(
  ANSWER_AUTO_SUBMIT_DELAY_MS / 1000,
);

const QUIZ_PLACEHOLDER_REEL_ITEM = {
  id: 'retry-quiz-placeholder',
  type: 'image' as const,
  src: '',
  duration: 1,
  title: 'Retry Knowledge Check',
  description:
    'Revisit the lesson audio and confirm the correct response to proceed.',
};

export const RetryQuizPage = () => {
  const router = useRouter();
  const toast = useToast();
  const captureAnalyticsEvent = useCaptureAnalyticsEvent();
  const {
    courseId,
    contentId,
    quizId: searchQuizId,
  } = useSearch({
    from: '/_user/courses/retry-quiz',
  });

  const [activeQuizId, setActiveQuizId] = useState<string | undefined>(
    searchQuizId,
  );

  const { data: unresolvedQuizzes, isPending: isLoadingUnresolvedQuizzes } =
    useQuery({
      ...courseQueries.unresolvedQuizzes(courseId ?? ''),
    });
  const queryClient = useQueryClient();

  const { data: courseDetail, isPending: isLoadingCourse } = useQuery({
    ...courseQueries.detail(courseId ?? ''),
  });

  const { data: contentDetail, isPending: isLoadingContent } = useQuery({
    ...courseQueries.contentDetail(contentId ?? ''),
    enabled: Boolean(contentId),
  });

  const quizList = unresolvedQuizzes ?? [];
  const totalQuestionCount = quizList.length;

  const currentQuiz = !activeQuizId
    ? undefined
    : quizList.find((quiz) => quiz.id === activeQuizId);

  const courseTitle = courseDetail?.title ?? 'Course';
  const contentTitle = contentDetail?.title ?? 'Knowledge Review';
  const questionTitle =
    currentQuiz?.title ?? currentQuiz?.question ?? 'Retry Knowledge Check';

  const currentQuizId = currentQuiz?.id;

  const hasDescriptionModalRef = useRef<string | null>(null);
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
  }, []);

  const setActiveQuiz = useCallback(
    (nextQuizId: string) => {
      clearInactivityTimer();
      setActiveQuizId(nextQuizId);
    },
    [clearInactivityTimer],
  );

  const {
    audioRef,
    state: playerState,
    hasSource,
    play: playAudio,
    pause: pauseAudio,
    toggleMute,
    previousQuizId,
    nextQuizId,
    playNextQuiz,
    playPreviousQuiz,
  } = useQuizAudioPlayer({
    quizzes: quizList,
    activeQuizId,
    onRequestQuizChange: setActiveQuiz,
  });

  const currentIndex = (() => {
    if (!quizList.length) return 0;
    if (!activeQuizId) return 0;
    const index = quizList.findIndex((quiz) => quiz.id === activeQuizId);
    return index >= 0 ? index : 0;
  })();

  const currentOrder = currentIndex + 1;

  const remainingCount = Math.max(totalQuestionCount - (currentIndex + 1), 0);

  const reelData = quizList.length
    ? quizList.map((quiz) => ({
        id: quiz.id,
        type: 'image' as const,
        src: pickPreferredMediaSource(quiz.media_sources)?.url ?? '',
        duration: Math.max(quiz.duration_secs ?? 0, 1),
        title: quiz.title ?? quiz.question ?? 'Retry Knowledge Check',
        description:
          quiz.description ??
          'Review the audio and confirm the correct answer to proceed.',
      }))
    : [QUIZ_PLACEHOLDER_REEL_ITEM];

  const handleReelIndexChange = useCallback(
    (nextIndex: number) => {
      const nextQuiz = quizList[nextIndex];
      if (!nextQuiz) return;
      setActiveQuiz(nextQuiz.id);
    },
    [quizList, setActiveQuiz],
  );

  useEffect(() => clearInactivityTimer, [clearInactivityTimer]);

  useEffect(() => {
    if (!quizList.length) return;

    const fallbackId =
      (searchQuizId && quizList.some((quiz) => quiz.id === searchQuizId)
        ? searchQuizId
        : quizList[0]?.id) ?? undefined;

    setActiveQuizId((previous) =>
      previous === fallbackId ? previous : fallbackId,
    );
  }, [quizList, searchQuizId]);

  useEffect(() => {
    if (!courseId || !contentId || !activeQuizId) return;
    if (searchQuizId === activeQuizId) return;

    router.navigate({
      to: '/courses/retry-quiz',
      search: {
        courseId,
        contentId,
        quizId: activeQuizId,
      },
      replace: true,
    });
  }, [activeQuizId, contentId, courseId, router, searchQuizId]);

  const { mutateAsync: submitAnswer, isPending: isSubmittingAnswer } =
    useMutation({ mutationFn: postQuizzesAnswer });

  const handleContinue = useCallback(() => {
    clearInactivityTimer();

    const overlayId = hasDescriptionModalRef.current;
    if (overlayId) {
      overlay.close(overlayId);
      hasDescriptionModalRef.current = null;
    }

    if (nextQuizId) {
      playNextQuiz();
      return;
    }

    pauseAudio();
    router.navigate({
      to: '/courses/quiz-grading',
      search: {
        courseId,
        contentId,
      },
    });
  }, [
    clearInactivityTimer,
    contentId,
    courseId,
    nextQuizId,
    pauseAudio,
    playNextQuiz,
    router,
  ]);

  const handleAnswer = useCallback(
    async (answer: boolean | null) => {
      if (!courseId || !contentId || !currentQuizId || isSubmittingAnswer) {
        return;
      }

      try {
        pauseAudio();
        clearInactivityTimer();

        const response = await submitAnswer({
          id: contentId,
          quiz_id: currentQuizId,
          answer_bool: answer,
        });
        captureAnalyticsEvent('quiz_answer_submitted', {
          course_id: courseId,
          content_id: contentId,
          quiz_id: currentQuizId,
          answer_value: answer === null ? 'skipped' : answer ? 'true' : 'false',
          is_correct: response.is_correct,
          attempt_type: 'retry',
          question_title: questionTitle,
        });
        if (contentId) {
          void queryClient.invalidateQueries({
            queryKey: courseQueries.quizzes(contentId).queryKey,
          });
        }

        hasDescriptionModalRef.current = overlay.open(({ isOpen }) => (
          <DescriptionModal
            open={isOpen}
            answer={answer}
            isCorrect={response.is_correct}
            remainingCount={remainingCount}
            questionTitle={questionTitle}
            questionDescription={''}
            explanationSource={pickPreferredMediaSource(
              response.answer_explanation_media_sources ?? undefined,
            )}
            onOpenChange={(open) => {
              if (!open) {
                clearInactivityTimer();
              }
            }}
            onContinue={handleContinue}
          />
        ));
      } catch (error) {
        const err = error as ErrorResponse | undefined;
        toast({
          type: 'error',
          title: 'Cannot submit answer',
          description: err?.data?.errors?.[0]?.detail ?? 'Please try again.',
        });
      }
    },
    [
      clearInactivityTimer,
      courseId,
      contentId,
      currentQuizId,
      handleContinue,
      pauseAudio,
      questionTitle,
      remainingCount,
      queryClient,
      currentOrder,
      currentQuiz?.knowledge_check_type,
      submitAnswer,
      toast,
      isSubmittingAnswer,
    ],
  );

  useEffect(() => {
    if (
      !playerState.isEnded ||
      !currentQuizId ||
      hasDescriptionModalRef.current
    ) {
      clearInactivityTimer();
      return;
    }

    clearInactivityTimer();
    inactivityTimerRef.current = setTimeout(() => {
      void handleAnswer(null);
    }, ANSWER_AUTO_SUBMIT_DELAY_MS);

    return clearInactivityTimer;
  }, [clearInactivityTimer, currentQuizId, handleAnswer, playerState.isEnded]);

  const [
    countDownAnswerAutoSubmitSeconds,
    setCountDownAnswerAutoSubmitSeconds,
  ] = useState(0);

  useEffect(() => {
    if (!playerState.isEnded) {
      setCountDownAnswerAutoSubmitSeconds(0);
      return;
    }

    setCountDownAnswerAutoSubmitSeconds(AUTO_SUBMIT_DELAY_SECONDS);
    const interval = setInterval(() => {
      setCountDownAnswerAutoSubmitSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }

        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [playerState.isEnded]);

  const isBusy = isSubmittingAnswer;
  const progressPercent = Number.isFinite(playerState.progressPercent)
    ? Math.min(Math.max(playerState.progressPercent, 0), 100)
    : 0;

  const isLoading =
    isLoadingUnresolvedQuizzes || isLoadingCourse || isLoadingContent;

  if (isLoading) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-slate-100 px-4 text-slate-700">
        <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-6 py-4 shadow-[0_20px_50px_rgba(15,23,42,0.15)]">
          <Loader2 className="h-5 w-5 animate-spin text-slate-500" />
          <span className="text-sm font-medium">
            Preparing retry session...
          </span>
        </div>
      </main>
    );
  }

  if (!totalQuestionCount) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-slate-100 px-4 text-slate-900">
        <section className="w-full max-w-lg space-y-6 rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-[0_24px_60px_rgba(15,23,42,0.12)]">
          <div className="flex justify-center">
            <div className="flex size-12 items-center justify-center rounded-full bg-slate-100 text-slate-500">
              <AlertCircle className="h-6 w-6" />
            </div>
          </div>
          <h1 className="text-2xl font-semibold">All caught up!</h1>
          <p className="text-sm text-slate-500">
            There are no remaining questions to retry. Return to the quiz
            summary to review your latest score.
          </p>
          <Button
            className="w-full rounded-xl"
            onClick={() =>
              router.navigate({
                to: '/courses/quiz-grading',
                search: {
                  courseId,
                  contentId,
                },
              })
            }
          >
            Back to Quiz Summary
          </Button>
        </section>
      </main>
    );
  }

  return (
    <main className="flex h-[calc(100vh-70px)] justify-center bg-slate-950 px-3 py-3">
      <Reel
        data={reelData}
        index={currentIndex}
        onIndexChange={handleReelIndexChange}
        defaultPlaying={false}
        playing={false}
        className="w-full max-w-[420px] rounded-[28px] border border-white/10 bg-slate-900 text-white shadow-[0_30px_80px_rgba(15,23,42,0.5)]"
      >
        <ReelContent>
          {() => (
            <ReelItem className="relative overflow-y-auto flex h-full flex-col bg-[radial-gradient(circle_at_top,rgba(239,68,68,0.28),transparent),linear-gradient(160deg,rgba(15,23,42,0.92),rgba(76,29,149,0.88))]">
              <div className="relative flex h-full flex-col">
                <div className="px-5 pt-5">
                  <div className="flex items-center justify-between gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-10 w-10 rounded-full border border-white/10 bg-white/10 text-white transition hover:bg-white/20"
                      onClick={() =>
                        router.navigate({
                          to: '/courses/quiz-grading',
                          search: {
                            courseId,
                            contentId,
                          },
                        })
                      }
                      aria-label="Back to quiz summary"
                    >
                      <ArrowLeft className="h-4 w-4" />
                    </Button>
                    <div className="flex items-center gap-1.5">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-10 w-10 rounded-full border border-white/10 bg-white/5 text-white transition hover:bg-white/15"
                        onClick={() => playPreviousQuiz()}
                        aria-label="Previous Quiz"
                        disabled={!previousQuizId}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-10 w-10 rounded-full border border-white/10 bg-white/5 text-white transition hover:bg-white/15"
                        onClick={() => playNextQuiz()}
                        aria-label="Next Quiz"
                        disabled={!nextQuizId}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="mt-3 space-y-1.5">
                    <Badge
                      variant="accent"
                      className="uppercase tracking-[0.18em]"
                    >
                      Retry Session
                    </Badge>
                    <div className="flex flex-col gap-1 text-xs uppercase tracking-[0.22em] text-white/60">
                      <span className="truncate pr-4">
                        {courseTitle} · {contentTitle}
                      </span>
                      <span>
                        Question {currentIndex + 1} / {totalQuestionCount || 1}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="px-5 mb-4">
                  <h2 className="mt-2 text-lg font-bold leading-tight text-white">
                    {questionTitle}
                  </h2>
                </div>

                <div className="flex flex-1 flex-col justify-end px-5 pb-5">
                  <div className="rounded-[20px] border border-white/10 bg-white/8 p-3 backdrop-blur">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5">
                        <Button
                          variant="glass"
                          size="icon"
                          className={cn('h-10 w-10 rounded-full text-white', {
                            'opacity-50': !hasSource,
                          })}
                          onClick={() =>
                            playerState.isPlaying ? pauseAudio() : playAudio()
                          }
                          disabled={!hasSource}
                          aria-label={
                            playerState.isPlaying ? 'Pause Audio' : 'Play Audio'
                          }
                        >
                          {playerState.isPlaying ? (
                            <Pause className="h-4 w-4" />
                          ) : (
                            <Play className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          variant="glass"
                          size="icon"
                          className={cn('h-10 w-10 rounded-full text-white', {
                            'opacity-50': !hasSource,
                          })}
                          onClick={toggleMute}
                          disabled={!hasSource}
                          aria-label={
                            playerState.isMuted ? 'Unmute Audio' : 'Mute Audio'
                          }
                        >
                          {playerState.isMuted ? (
                            <VolumeX className="h-4 w-4" />
                          ) : (
                            <Volume2 className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                      <span className="text-xs font-medium uppercase tracking-[0.22em] text-white/60">
                        Retry Playback
                      </span>
                    </div>
                    {hasSource ? (
                      <div className="mt-4 flex items-center gap-2 text-xs text-white/70">
                        <span>{formatTimestamp(playerState.currentTime)}</span>
                        <div className="relative h-1 flex-1 overflow-hidden rounded-full bg-white/15">
                          <div
                            className="absolute inset-y-0 left-0 rounded-full bg-white"
                            style={{ width: `${progressPercent}%` }}
                          />
                        </div>
                        <span>{formatTimestamp(playerState.duration)}</span>
                      </div>
                    ) : (
                      <p className="mt-3 text-xs text-amber-300">
                        No audio is provided for this retry question.
                      </p>
                    )}
                  </div>

                  <div className="flex flex-1 flex-col mt-20 justify-between">
                    <div className="grid gap-3 grid-cols-2 flex-1">
                      <Button
                        variant="glow"
                        size="lg"
                        className={cn(
                          'rounded-[20px] text-xl font-bold uppercase h-full min-h-[200px]',
                          {
                            'pointer-events-none opacity-60': isBusy,
                          },
                        )}
                        disabled={isBusy}
                        onClick={() => {
                          handleAnswer(true);
                        }}
                      >
                        Yes
                      </Button>
                      <Button
                        variant="destructive"
                        size="lg"
                        className={cn(
                          'rounded-[20px] text-xl font-bold uppercase h-full min-h-[200px]',
                          {
                            'pointer-events-none opacity-70': isBusy,
                          },
                        )}
                        disabled={isBusy}
                        onClick={() => {
                          handleAnswer(false);
                        }}
                      >
                        No
                      </Button>
                    </div>
                    <p className="mt-2 text-[11px] text-white/40">
                      If no answer is submitted within{' '}
                      {AUTO_SUBMIT_DELAY_SECONDS} seconds after the audio ends,
                      the explanation will open automatically.{' '}
                      {playerState.isEnded
                        ? `(${countDownAnswerAutoSubmitSeconds}s)`
                        : ''}
                    </p>
                  </div>
                </div>
              </div>
            </ReelItem>
          )}
        </ReelContent>
      </Reel>
      <audio ref={audioRef} className="hidden" />
    </main>
  );
};
